import t from 'react-translate';
import NvIcon from 'shared/components/nv-icon';
import NvTooltip from 'shared/components/nv-tooltip';
import NvSwitch from 'shared/components/inputs/nv-switch';
import NvPopover from 'shared/components/nv-popover';
import NvConfirmationPopover from 'shared/components/nv-confirmation-popover';
import { css } from '@emotion/react';
import { getInputTypeLabel } from 'athena/components/mentoring-program/constants';
import { InputType } from 'redux/schemas/models/mentoring-program-profile-questions';
import { useState } from 'react';
import { config } from '@config/pendo.config.json';
import { OrgProfileFieldType } from './org-level-fields';

const overlayStyles = css`
  max-width: 280px;
  .confirmation {
    max-width: 100%;
    .button-container {
      justify-content: center;
    }
  }
`;

const NonEditableField = () => (
  <div className='d-flex align-items-center not-editable'>
    <div className='pr-1 field-type italic text-regular'>
      {t.MENTORING_PROGRAMS.SETTINGS.TABS.PROGRAM_PROFILE_SETUP.ORG_LEVEL_FIELDS.NOT_EDITABLE.LABEL()}
    </div>
    <NvTooltip
      text={t.MENTORING_PROGRAMS.SETTINGS.TABS.PROGRAM_PROFILE_SETUP.ORG_LEVEL_FIELDS.NOT_EDITABLE.TOOLTIP()}
    >
      <NvIcon icon='info' size='xss-smallest' />
    </NvTooltip>
  </div>
);

type EditableFieldProps = {
  id: number;
  name: string;
  inputType: string;
  isRequired: boolean;
  isAttachedToRule: boolean;
  onChange: (
    isRequired: boolean,
    shouldUpdateMatchingRules: boolean
  ) => void;
};

const EditableField = (props: EditableFieldProps) => {
  const { id, name, inputType, isRequired, isAttachedToRule, onChange } = props;
  const [isSwitchingRequired, setIsSwitchingRequired] = useState(false);
  const translationBase = t.MENTORING_PROGRAMS.SETTINGS.TABS.PROGRAM_PROFILE_SETUP;
  const popoverTranslationBase = translationBase.PROGRAM_LEVEL_FIELDS.REQUIRED_POPOVER;

  const handleChange = (newState: boolean) => {
    if (!newState && isAttachedToRule) {
      setIsSwitchingRequired(true);
    } else onChange(newState, false);
  };

  const dataQATag = config.pendo.athena.mentorshipProgram.settings.programProfileSetup.orgLevelFieldSwitch;

  return (
    <>
      <div className='field-type text-regular'>{getInputTypeLabel(t, inputType as unknown as InputType)}</div>
      <NvPopover
        enabled
        show={isSwitchingRequired}
        overlayStyles={overlayStyles}
        content={(
          <NvConfirmationPopover
            onCancel={() => setIsSwitchingRequired(false)}
            onConfirm={() => {
              onChange(false, true);
              setIsSwitchingRequired(false);
            }}
            header={popoverTranslationBase.TITLE()}
            message={popoverTranslationBase.DESCRIPTION()}
            cancelBtnText={popoverTranslationBase.CANCEL()}
            confirmBtnText={popoverTranslationBase.OK()}
          />
        )}
        placement='top'
        rootClose
        onHide={() => setIsSwitchingRequired(false)}
        className='d-inline-flex mw-100 justify-content-center'
        preventOverflow
      >
        <NvSwitch
          className='cell'
          checked={isRequired}
          onChange={handleChange}
          dataQa={dataQATag}
          dataQaId={`${dataQATag}_${id}`}
          ariaLabel={
            isRequired
              ? translationBase.ARIA_LABEL.DISABLE_FIELD(name)
              : translationBase.ARIA_LABEL.ENABLE_FIELD(name)
          }
        />
      </NvPopover>
    </>
  );
};

type OrgLevelFieldRowProps = {
  field: OrgProfileFieldType;
  onChange: (
    isRequired: boolean,
    shouldUpdateMatchingRules: boolean
  ) => void;
};

const OrgLevelFieldRow = ({ field, onChange }: OrgLevelFieldRowProps) => {
  const { programQuestionId, name, inputType, isCsvManaged, isIntegrated, isRequired, isAttachedToRule } = field;

  return (
    <div className='align-content-center field-row'>
      <div className='d-flex field-name text-regular semi-bold'>
        <span>{name}</span>
        {isRequired && <span className='required'>*</span>}
      </div>
      {isCsvManaged || isIntegrated ? (
        <NonEditableField />
      ) : (
        <EditableField
          id={programQuestionId}
          inputType={inputType}
          isRequired={isRequired}
          isAttachedToRule={isAttachedToRule}
          onChange={onChange}
          name={name}
        />
      )}
    </div>
  );
};

export default OrgLevelFieldRow;
